import { FiltersBar, type FiltersBarFilter } from '@deepdive-ui';
import { useMemo, useState } from 'react';

import type { FiltersType } from '@/data-sources/contract';
import { useDataSource } from '@/features/data/DataSourceContext';
import { FILTER_CHIPS } from '@/features/filters/chips';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setSelection } from '@/store/slices/filtersSlice';

/** The filters bar, wired to the source's filter state. Absent selection = every option ticked. */
export function FiltersBarContainer({ type }: { type: FiltersType }) {
  const { filterScope, bootstrap } = useDataSource();
  const selections = useAppSelector((state) => state.filters[filterScope]?.selections);
  const dispatch = useAppDispatch();
  // Which chip's panel is open is UI state, not data (prototype: App.openFilterId).
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);

  const filters = useMemo<FiltersBarFilter[]>(
    () =>
      FILTER_CHIPS.map((chip) => {
        const options = bootstrap.filterOptions[chip.dimension].map((o) => ({
          value: o.value,
          label: o.label,
        }));
        const allValues = options.map((o) => o.value);
        return {
          id: chip.id,
          label: chip.label,
          icon: chip.icon,
          types: chip.types,
          options,
          selected: [...(selections?.[chip.dimension] ?? allValues)],
          onSelectionChange: (values: string[]) =>
            dispatch(
              setSelection({ scope: filterScope, dimension: chip.dimension, values, allValues }),
            ),
        };
      }),
    [bootstrap.filterOptions, selections, dispatch, filterScope],
  );

  return (
    <FiltersBar
      filters={filters}
      type={type}
      openFilterId={openFilterId}
      onOpenFilterChange={setOpenFilterId}
    />
  );
}
